import { css } from "styled-system/css";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { AdminPageShell } from "@/components/admin/admin-page-shell";
import { AccountStatusBadge, type RealStatus } from "@/components/admin/account-status-badge";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { EmptyState } from "@/components/admin/empty-state";

type ReferralCodeDetail = {
  code: {
    id: string;
    code: string;
    ownerLabel: string;
    active: boolean;
    createdBy: string | null;
    createdAt: string;
  };
  professionals: {
    id: string;
    name: string;
    slug: string;
    email: string | null;
    plan: string;
    planExpiresAt: string | null;
    isActive: boolean;
    createdAt: string;
    realStatus: RealStatus;
  }[];
};

export function AdminReferralCodeDetail() {
  const { id } = useParams<{ id: string }>();
  const { refresh } = useAuth();
  const [data, setData] = useState<ReferralCodeDetail | null>(null);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    const r = await fetchWithSessionSync(`/api/admin/referral-codes/${id}`, { credentials: "include" }, refresh);
    if (r.status === 404) {
      setNotFound(true);
      return;
    }
    if (r.ok) setData((await r.json()) as ReferralCodeDetail);
  }, [id, refresh]);

  useEffect(() => {
    void load();
  }, [load]);

  if (notFound) {
    return (
      <AdminPageShell title="Código de referido">
        <EmptyState title="Código no encontrado" description="Puede haber sido eliminado." icon="🔗" />
      </AdminPageShell>
    );
  }

  if (!data) {
    return (
      <AdminPageShell title="Código de referido">
        <LoadingFrame variant="light" minHeight={320} />
      </AdminPageShell>
    );
  }

  return (
    <AdminPageShell
      title={data.code.code}
      description={data.code.ownerLabel}
      actions={
        <Link
          to="/admin/referral-codes"
          className={css({ display: "inline-flex", alignItems: "center", gap: "1.5", fontSize: "sm", color: "fg.muted", _hover: { color: "fg.DEFAULT" } })}
        >
          <ArrowLeft size={16} />
          Volver a Códigos de referido
        </Link>
      }
    >
      {data.professionals.length === 0 ? (
        <EmptyState
          title="Todavía no se registró nadie con este código"
          description="Cuando alguien se registre usando este código, va a aparecer acá."
          icon="🔗"
        />
      ) : (
        <div className={css({ overflowX: "auto", rounded: "xl", borderWidth: "1px", borderColor: "border.DEFAULT", bg: "bg.card" })}>
          <table className={css({ w: "full", borderCollapse: "collapse", fontSize: "sm" })}>
            <thead>
              <tr className={css({ bg: "bg.muted", textAlign: "left" })}>
                <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted" })}>Nombre</th>
                <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted" })}>Email</th>
                <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted" })}>Plan</th>
                <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted" })}>Estado</th>
                <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted" })}>Registrado</th>
              </tr>
            </thead>
            <tbody>
              {data.professionals.map((p) => (
                <tr key={p.id} className={css({ borderTopWidth: "1px", borderColor: "border.DEFAULT", _hover: { bg: "bg.muted" } })}>
                  <td className={css({ py: "3", px: "4", fontWeight: "semibold" })}>
                    <Link to={`/admin/professionals/${p.id}`} className={css({ color: "fg.DEFAULT", _hover: { textDecoration: "underline" } })}>
                      {p.name}
                    </Link>
                  </td>
                  <td className={css({ py: "3", px: "4", color: "fg.muted" })}>{p.email ?? "—"}</td>
                  <td className={css({ py: "3", px: "4" })}>{p.plan}</td>
                  <td className={css({ py: "3", px: "4" })}>
                    <AccountStatusBadge status={p.realStatus} />
                  </td>
                  <td className={css({ py: "3", px: "4", color: "fg.muted" })}>
                    {new Date(p.createdAt).toLocaleDateString("es-AR")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminPageShell>
  );
}
